/**
 * MeetingMinutesTab Component
 *
 * Captures meeting notes, action items, and decisions.
 * Data is persisted to Gobodo via Meeting_Agenda__c object (Minutes__c field).
 */

import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  Calendar,
  FileText,
  CheckCircle2,
  Lightbulb,
  Plus,
  Trash2,
  User,
  Copy,
  Check,
  Clock,
  Loader2,
  Cloud,
  CloudOff,
} from 'lucide-react';
import { useUsers, User as UserType } from '../../services/queries/userQueries';
import {
  useMeetingAgenda,
  useCreateMeetingAgenda,
  useUpdateMeetingAgenda,
  MeetingActionItem,
  MeetingDecision,
  MeetingMinutesData,
} from '../../services/queries/cyberSweepQueries';
import { filterAttendanceEligibleUsers } from '../../utils/cyberSweepUserFilters';

interface MeetingMinutesTabProps {
  selectedWeek: string; // Format: YYYY-Www
  weekStartDate: string; // Monday date in yyyy-MM-dd format
}

// Re-export types for local use (imported from cyberSweepQueries)
type ActionItem = MeetingActionItem;
type Decision = MeetingDecision;

interface LocalMeetingMinutes {
  id: string;
  weekStartDate: string;
  meetingDate: string;
  discussionNotes: string;
  actionItems: ActionItem[];
  decisions: Decision[];
  createdBy: string;
  createdAt: string;
  lastModifiedBy: string;
  lastModifiedAt: string;
}

// Generate unique ID
const generateId = (): string => {
  return `minutes-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
};

// Format date for display (MM/DD/YYYY)
const formatDateForDisplay = (isoDate: string): string => {
  if (!isoDate) return '';
  try {
    const datePart = isoDate.includes('T') ? isoDate.split('T')[0] : isoDate;
    const [year, month, day] = datePart.split('-');
    if (year && month && day) {
      return `${month}/${day}/${year}`;
    }
    return isoDate;
  } catch {
    return isoDate;
  }
};

// Format datetime for display
const formatDateTime = (isoDate: string): string => {
  if (!isoDate) return '';
  try {
    const date = new Date(isoDate);
    return date.toLocaleString('en-US', {
      month: '2-digit',
      day: '2-digit',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    });
  } catch {
    return isoDate;
  }
};

// Create default meeting minutes
const createDefaultMinutes = (weekStartDate: string): LocalMeetingMinutes => ({
  id: generateId(),
  weekStartDate,
  meetingDate: weekStartDate,
  discussionNotes: '',
  actionItems: [],
  decisions: [],
  createdBy: '',
  createdAt: '',
  lastModifiedBy: '',
  lastModifiedAt: '',
});

// Debounce helper - simple implementation for auto-save
function createDebouncedSave<T>(fn: (data: T) => void, delay: number): { save: (data: T) => void; cancel: () => void } {
  let timeoutId: ReturnType<typeof setTimeout> | null = null;
  return {
    save: (data: T) => {
      if (timeoutId) clearTimeout(timeoutId);
      timeoutId = setTimeout(() => fn(data), delay);
    },
    cancel: () => {
      if (timeoutId) clearTimeout(timeoutId);
    },
  };
}

const MeetingMinutesTab: React.FC<MeetingMinutesTabProps> = ({ selectedWeek, weekStartDate }) => {
  const { data: allUsers = [] } = useUsers({});
  // Filter to active CyberSweep users for action item assignment
  const cyberSweepUsers = useMemo(
    () => filterAttendanceEligibleUsers(allUsers),
    [allUsers]
  );

  // Gobodo hooks - reuse the same meeting agenda record
  const { data: agendaRecord, isLoading: isLoadingAgenda } = useMeetingAgenda(weekStartDate);
  const createAgenda = useCreateMeetingAgenda();
  const updateAgenda = useUpdateMeetingAgenda();

  // State
  const [minutes, setMinutes] = useState<LocalMeetingMinutes>(() => createDefaultMinutes(weekStartDate));
  const [copySuccess, setCopySuccess] = useState(false);
  const [saveStatus, setSaveStatus] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle');

  // Track if data has been loaded from Gobodo
  const isDataLoaded = useRef(false);

  // Current user (mock for now)
  const currentUser = 'Current User';

  // Initialize from Gobodo data when loaded
  useEffect(() => {
    if (agendaRecord && !isDataLoaded.current) {
      console.log('[MeetingMinutesTab] Loading data from Gobodo:', agendaRecord);
      const minutesData = agendaRecord.minutes;
      setMinutes({
        id: agendaRecord.id,
        weekStartDate: agendaRecord.weekStartDate,
        meetingDate: agendaRecord.meetingDate || weekStartDate,
        discussionNotes: minutesData?.discussionNotes || '',
        actionItems: minutesData?.actionItems || [],
        decisions: minutesData?.decisions || [],
        createdBy: agendaRecord.createdBy || '',
        createdAt: agendaRecord.createdDate || '',
        lastModifiedBy: minutesData?.lastModifiedBy || agendaRecord.lastModifiedBy || '',
        lastModifiedAt: minutesData?.lastModifiedAt || agendaRecord.lastModifiedDate || '',
      });
      isDataLoaded.current = true;
    }
  }, [agendaRecord, weekStartDate]);

  // Reset state when week changes
  useEffect(() => {
    isDataLoaded.current = false;
    setMinutes(createDefaultMinutes(weekStartDate));
    setSaveStatus('idle');
    console.log('[MeetingMinutesTab] Week changed, reset to defaults:', weekStartDate);
  }, [weekStartDate]);

  // Save function - creates or updates minutes in Gobodo
  const saveToGobodo = useCallback(async (minutesData: MeetingMinutesData) => {
    setSaveStatus('saving');
    try {
      if (agendaRecord?.id) {
        // Update existing record
        await updateAgenda.mutateAsync({
          id: agendaRecord.id,
          weekStartDate,
          minutes: minutesData,
        });
      } else {
        // Create new record with minutes
        await createAgenda.mutateAsync({
          weekStartDate,
          meetingDate: minutes.meetingDate,
          minutes: minutesData,
        });
      }
      setSaveStatus('saved');
      setTimeout(() => setSaveStatus('idle'), 2000);
    } catch (error) {
      console.error('[MeetingMinutesTab] Save error:', error);
      setSaveStatus('error');
      setTimeout(() => setSaveStatus('idle'), 3000);
    }
  }, [agendaRecord, weekStartDate, minutes.meetingDate, updateAgenda, createAgenda]);

  // Debounced auto-save (1 second delay)
  const debouncedSave = useMemo(
    () => createDebouncedSave<MeetingMinutesData>(
      (minutesData) => saveToGobodo(minutesData),
      1000
    ),
    [saveToGobodo]
  );

  // Cleanup debounced save on unmount
  useEffect(() => {
    return () => {
      debouncedSave.cancel();
    };
  }, [debouncedSave]);

  // Helper to trigger save with current minutes data
  const triggerSave = useCallback((updatedMinutes: LocalMeetingMinutes) => {
    const minutesData: MeetingMinutesData = {
      discussionNotes: updatedMinutes.discussionNotes,
      actionItems: updatedMinutes.actionItems,
      decisions: updatedMinutes.decisions,
      lastModifiedAt: new Date().toISOString(),
      lastModifiedBy: currentUser,
    };
    debouncedSave.save(minutesData);
  }, [debouncedSave, currentUser]);

  // Update meeting date with auto-save
  const updateMeetingDate = (date: string) => {
    const updated = {
      ...minutes,
      meetingDate: date,
      lastModifiedBy: currentUser,
      lastModifiedAt: new Date().toISOString(),
    };
    setMinutes(updated);
    triggerSave(updated);
  };

  // Update discussion notes with auto-save
  const updateDiscussionNotes = (notes: string) => {
    const updated = {
      ...minutes,
      discussionNotes: notes,
      lastModifiedBy: currentUser,
      lastModifiedAt: new Date().toISOString(),
    };
    setMinutes(updated);
    triggerSave(updated);
  };

  // Add action item with auto-save
  const addActionItem = () => {
    const newItem: ActionItem = {
      id: generateId(),
      description: '',
      assignedToId: '',
      assignedToName: '',
      dueDate: '',
      isComplete: false,
    };
    const updated = {
      ...minutes,
      actionItems: [...minutes.actionItems, newItem],
      lastModifiedBy: currentUser,
      lastModifiedAt: new Date().toISOString(),
    };
    setMinutes(updated);
    triggerSave(updated);
  };

  // Update action item with auto-save
  const updateActionItem = (itemId: string, updates: Partial<ActionItem>) => {
    const updated = {
      ...minutes,
      actionItems: minutes.actionItems.map(item =>
        item.id === itemId ? { ...item, ...updates } : item
      ),
      lastModifiedBy: currentUser,
      lastModifiedAt: new Date().toISOString(),
    };
    setMinutes(updated);
    triggerSave(updated);
  };

  // Delete action item with auto-save
  const deleteActionItem = (itemId: string) => {
    const updated = {
      ...minutes,
      actionItems: minutes.actionItems.filter(item => item.id !== itemId),
      lastModifiedBy: currentUser,
      lastModifiedAt: new Date().toISOString(),
    };
    setMinutes(updated);
    triggerSave(updated);
  };

  // Toggle action item completion with auto-save
  const toggleActionItemComplete = (itemId: string) => {
    const updated = {
      ...minutes,
      actionItems: minutes.actionItems.map(item =>
        item.id === itemId ? { ...item, isComplete: !item.isComplete } : item
      ),
      lastModifiedBy: currentUser,
      lastModifiedAt: new Date().toISOString(),
    };
    setMinutes(updated);
    triggerSave(updated);
  };

  // Add decision with auto-save
  const addDecision = () => {
    const newDecision: Decision = {
      id: generateId(),
      description: '',
      decisionDate: new Date().toISOString().split('T')[0],
    };
    const updated = {
      ...minutes,
      decisions: [...minutes.decisions, newDecision],
      lastModifiedBy: currentUser,
      lastModifiedAt: new Date().toISOString(),
    };
    setMinutes(updated);
    triggerSave(updated);
  };

  // Update decision with auto-save
  const updateDecision = (decisionId: string, updates: Partial<Decision>) => {
    const updated = {
      ...minutes,
      decisions: minutes.decisions.map(d =>
        d.id === decisionId ? { ...d, ...updates } : d
      ),
      lastModifiedBy: currentUser,
      lastModifiedAt: new Date().toISOString(),
    };
    setMinutes(updated);
    triggerSave(updated);
  };

  // Delete decision with auto-save
  const deleteDecision = (decisionId: string) => {
    const updated = {
      ...minutes,
      decisions: minutes.decisions.filter(d => d.id !== decisionId),
      lastModifiedBy: currentUser,
      lastModifiedAt: new Date().toISOString(),
    };
    setMinutes(updated);
    triggerSave(updated);
  };

  // Copy minutes to clipboard
  const copyToClipboard = useCallback(async () => {
    const actionItemsText = minutes.actionItems.length > 0
      ? minutes.actionItems.map(item =>
          `- [${item.isComplete ? 'x' : ' '}] ${item.description || '(No description)'} | Assigned: ${item.assignedToName || 'Unassigned'} | Due: ${formatDateForDisplay(item.dueDate) || 'No date'}`
        ).join('\n')
      : '(No action items)';

    const decisionsText = minutes.decisions.length > 0
      ? minutes.decisions.map(d =>
          `- ${d.description || '(No description)'} (${formatDateForDisplay(d.decisionDate)})`
        ).join('\n')
      : '(No decisions recorded)';

    const text = `
MEETING MINUTES
===============
Week: ${selectedWeek}
Meeting Date: ${formatDateForDisplay(minutes.meetingDate)}

DISCUSSION NOTES
----------------
${minutes.discussionNotes || '(No notes recorded)'}

ACTION ITEMS
------------
${actionItemsText}

DECISIONS MADE
--------------
${decisionsText}

---
Generated: ${formatDateTime(new Date().toISOString())}
`.trim();

    try {
      await navigator.clipboard.writeText(text);
      setCopySuccess(true);
      setTimeout(() => setCopySuccess(false), 2000);
    } catch (error) {
      console.error('[MeetingMinutesTab] Failed to copy to clipboard:', error);
    }
  }, [minutes, selectedWeek]);

  // Stats
  const completedActions = minutes.actionItems.filter(a => a.isComplete).length;
  const totalActions = minutes.actionItems.length;

  // Loading state
  if (isLoadingAgenda) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
        <div className="flex items-center justify-center py-8">
          <Loader2 className="h-8 w-8 text-purple-600 animate-spin" />
          <span className="ml-2 text-gray-500 dark:text-gray-400">Loading meeting minutes...</span>
        </div>
      </div>
    );
  }

  // Save status indicator
  const SaveStatusIndicator = () => {
    if (saveStatus === 'saving') {
      return (
        <div className="flex items-center gap-1.5 text-xs text-blue-600 dark:text-blue-400">
          <Loader2 className="h-3.5 w-3.5 animate-spin" />
          <span>Saving...</span>
        </div>
      );
    }
    if (saveStatus === 'saved') {
      return (
        <div className="flex items-center gap-1.5 text-xs text-green-600 dark:text-green-400">
          <Cloud className="h-3.5 w-3.5" />
          <span>Saved</span>
        </div>
      );
    }
    if (saveStatus === 'error') {
      return (
        <div className="flex items-center gap-1.5 text-xs text-red-600 dark:text-red-400">
          <CloudOff className="h-3.5 w-3.5" />
          <span>Save failed</span>
        </div>
      );
    }
    return null;
  };

  return (
    <div className="space-y-4">
      {/* Meeting Date Header */}
      <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-2">
              <Calendar className="h-5 w-5 text-purple-600 dark:text-purple-400" />
              <h3 className="font-medium text-gray-900 dark:text-gray-100">Meeting Date</h3>
            </div>
            <span className="text-sm text-gray-500 dark:text-gray-400 bg-gray-100 dark:bg-gray-700 px-2 py-0.5 rounded">
              Week {selectedWeek.split('-W')[1]}
            </span>
            <SaveStatusIndicator />
          </div>
          <div className="flex items-center gap-3">
            <input
              type="date"
              value={minutes.meetingDate}
              onChange={(e) => updateMeetingDate(e.target.value)}
              className="px-3 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
            />
            <button
              onClick={copyToClipboard}
              className={`flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-lg transition-colors ${
                copySuccess
                  ? 'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300'
                  : 'text-purple-600 dark:text-purple-400 hover:bg-purple-50 dark:hover:bg-purple-900/20'
              }`}
            >
              {copySuccess ? (
                <>
                  <Check className="h-4 w-4" />
                  Copied!
                </>
              ) : (
                <>
                  <Copy className="h-4 w-4" />
                  Copy to Clipboard
                </>
              )}
            </button>
          </div>
        </div>
        <p className="mt-1 text-sm text-gray-500 dark:text-gray-400 ml-7">
          {formatDateForDisplay(minutes.meetingDate)}
        </p>
      </div>

      {/* Discussion Notes */}
      <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
        <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-700 flex items-center gap-2">
          <FileText className="h-5 w-5 text-purple-600 dark:text-purple-400" />
          <h3 className="font-medium text-gray-900 dark:text-gray-100">Discussion Notes</h3>
        </div>
        <div className="p-4">
          <textarea
            value={minutes.discussionNotes}
            onChange={(e) => updateDiscussionNotes(e.target.value)}
            placeholder="Enter meeting discussion notes, key points, and topics covered..."
            rows={8}
            className="w-full px-3 py-2 text-sm border border-gray-200 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 placeholder-gray-400 resize-y focus:ring-1 focus:ring-purple-500 focus:border-purple-500"
          />
        </div>
      </div>

      {/* Action Items */}
      <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
        <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <CheckCircle2 className="h-5 w-5 text-purple-600 dark:text-purple-400" />
            <h3 className="font-medium text-gray-900 dark:text-gray-100">Action Items</h3>
          </div>
          <span className="text-sm text-gray-500 dark:text-gray-400">
            {completedActions}/{totalActions} completed
          </span>
        </div>

        {minutes.actionItems.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 dark:bg-gray-900/50 text-left text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                <tr>
                  <th className="px-3 py-2 font-medium w-10">Done</th>
                  <th className="px-3 py-2 font-medium">Description</th>
                  <th className="px-3 py-2 font-medium w-44">Assigned To</th>
                  <th className="px-3 py-2 font-medium w-36">Due Date</th>
                  <th className="px-3 py-2 font-medium w-16">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                {minutes.actionItems.map(item => (
                  <tr key={item.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/30">
                    {/* Checkbox */}
                    <td className="px-3 py-2">
                      <input
                        type="checkbox"
                        checked={item.isComplete}
                        onChange={() => toggleActionItemComplete(item.id)}
                        className="h-4 w-4 rounded border-gray-300 text-green-600 focus:ring-green-500 cursor-pointer"
                      />
                    </td>

                    {/* Description */}
                    <td className="px-3 py-2">
                      <input
                        type="text"
                        value={item.description}
                        onChange={(e) => updateActionItem(item.id, { description: e.target.value })}
                        placeholder="Enter action item..."
                        className={`w-full px-2 py-1 text-sm border border-gray-200 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 ${
                          item.isComplete ? 'line-through text-gray-400 dark:text-gray-500' : ''
                        }`}
                      />
                    </td>

                    {/* Assigned To */}
                    <td className="px-3 py-2">
                      <select
                        value={item.assignedToId}
                        onChange={(e) => {
                          const selectedUser = cyberSweepUsers.find(u => u.id === e.target.value);
                          updateActionItem(item.id, {
                            assignedToId: e.target.value,
                            assignedToName: selectedUser ? `${selectedUser.firstName} ${selectedUser.lastName}` : '',
                          });
                        }}
                        className="w-full px-2 py-1 text-sm border border-gray-200 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                      >
                        <option value="">Select person...</option>
                        {cyberSweepUsers.map(user => (
                          <option key={user.id} value={user.id}>
                            {user.firstName} {user.lastName}
                          </option>
                        ))}
                      </select>
                    </td>

                    {/* Due Date */}
                    <td className="px-3 py-2">
                      <input
                        type="date"
                        value={item.dueDate}
                        onChange={(e) => updateActionItem(item.id, { dueDate: e.target.value })}
                        className="px-2 py-1 text-sm border border-gray-200 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                      />
                    </td>

                    {/* Delete */}
                    <td className="px-3 py-2">
                      <button
                        onClick={() => deleteActionItem(item.id)}
                        className="p-1 text-gray-400 hover:text-red-500 dark:hover:text-red-400 rounded transition-colors"
                        title="Delete action item"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="px-4 py-6 text-center text-sm text-gray-500 dark:text-gray-400">
            No action items added yet
          </div>
        )}

        {/* Add Action Item Button */}
        <div className="px-4 py-3 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/30">
          <button
            onClick={addActionItem}
            className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-purple-600 dark:text-purple-400 hover:bg-purple-50 dark:hover:bg-purple-900/20 rounded-lg transition-colors"
          >
            <Plus className="h-4 w-4" />
            Add Action Item
          </button>
        </div>
      </div>

      {/* Decisions Made */}
      <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
        <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Lightbulb className="h-5 w-5 text-purple-600 dark:text-purple-400" />
            <h3 className="font-medium text-gray-900 dark:text-gray-100">Decisions Made</h3>
          </div>
          <span className="text-sm text-gray-500 dark:text-gray-400">
            {minutes.decisions.length} decision{minutes.decisions.length !== 1 ? 's' : ''}
          </span>
        </div>

        {minutes.decisions.length > 0 ? (
          <div className="divide-y divide-gray-200 dark:divide-gray-700">
            {minutes.decisions.map(decision => (
              <div
                key={decision.id}
                className="px-4 py-3 flex items-start gap-3 hover:bg-gray-50 dark:hover:bg-gray-700/50 group"
              >
                <div className="flex-1">
                  <input
                    type="text"
                    value={decision.description}
                    onChange={(e) => updateDecision(decision.id, { description: e.target.value })}
                    placeholder="Enter decision made..."
                    className="w-full px-2 py-1 text-sm border border-gray-200 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                  />
                </div>
                <div className="flex items-center gap-2">
                  <input
                    type="date"
                    value={decision.decisionDate}
                    onChange={(e) => updateDecision(decision.id, { decisionDate: e.target.value })}
                    className="px-2 py-1 text-sm border border-gray-200 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                  />
                  <button
                    onClick={() => deleteDecision(decision.id)}
                    className="p-1 text-gray-400 hover:text-red-500 dark:hover:text-red-400 opacity-0 group-hover:opacity-100 transition-opacity rounded"
                    title="Delete decision"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="px-4 py-6 text-center text-sm text-gray-500 dark:text-gray-400">
            No decisions recorded yet
          </div>
        )}

        {/* Add Decision Button */}
        <div className="px-4 py-3 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/30">
          <button
            onClick={addDecision}
            className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-purple-600 dark:text-purple-400 hover:bg-purple-50 dark:hover:bg-purple-900/20 rounded-lg transition-colors"
          >
            <Plus className="h-4 w-4" />
            Add Decision
          </button>
        </div>
      </div>

      {/* System Information Footer */}
      {(minutes.createdBy || minutes.lastModifiedBy) && (
        <div className="bg-gray-50 dark:bg-gray-900/30 rounded-lg border border-gray-200 dark:border-gray-700 px-4 py-3">
          <div className="flex items-center gap-6 text-xs text-gray-500 dark:text-gray-400">
            {minutes.createdBy && (
              <div className="flex items-center gap-1.5">
                <User className="h-3.5 w-3.5" />
                <span>
                  Created by {minutes.createdBy} on {formatDateTime(minutes.createdAt)}
                </span>
              </div>
            )}
            {minutes.lastModifiedBy && minutes.lastModifiedAt !== minutes.createdAt && (
              <div className="flex items-center gap-1.5">
                <Clock className="h-3.5 w-3.5" />
                <span>
                  Last modified by {minutes.lastModifiedBy} on {formatDateTime(minutes.lastModifiedAt)}
                </span>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Sync Status Footer */}
      {agendaRecord && (
        <div className="bg-green-50 dark:bg-green-900/20 rounded-lg border border-green-200 dark:border-green-800 px-4 py-2">
          <div className="flex items-center gap-2 text-xs text-green-700 dark:text-green-300">
            <Cloud className="h-3.5 w-3.5" />
            <span>Data synced with Gobodo • Auto-saves on changes</span>
          </div>
        </div>
      )}
    </div>
  );
};

export default MeetingMinutesTab;
